import {
  Box,
  FormControl,
  FormErrorMessage,
  Input,
  List,
  ListItem,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Control, Controller, RegisterOptions, UseFormSetValue } from 'react-hook-form';
import { getAccounts } from 'services/api.service';
import { maskPixKey, unMaskPixKey } from 'utils/pix';
import { onlyNumbers } from 'utils/number';
import FormLabelCustom from './FormLabel';

interface AccountResult {
  id: string;
  accountHolder: string | null;
  pix: string | null;
  agency: string | null;
  account: string | null;
  bankIspb: string | null;
}

interface PixKeyAutocompleteProps {
  name: string;
  label: string;
  control: Control<any, object>;
  setValue: UseFormSetValue<any>;
  setPixTypeKey: (type: 'CPF' | 'PHONE' | null) => void;
  onSelectAccount: (account: AccountResult | null) => void;
  registerOptions?: RegisterOptions;
}

export default function PixKeyAutocomplete({
  name,
  label,
  control,
  setValue,
  setPixTypeKey,
  onSelectAccount,
  registerOptions = {},
}: PixKeyAutocompleteProps) {
  const [suggestions, setSuggestions] = useState<AccountResult[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const searchAccounts = useCallback(async (text: string) => {
    if (text.length < 2) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    setLoading(true);
    try {
      const res = await getAccounts({ filter: text, isFavorite: true, limit: 5 });
      setSuggestions(res.registers || []);
      setShowDropdown((res.registers || []).length > 0);
    } catch {
      setSuggestions([]);
      setShowDropdown(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (value: string, fieldOnChange: (v: string) => void) => {
    const unmasked = unMaskPixKey(value);
    const masked = maskPixKey(
      ['CPF', 'CNPJ', 'PHONE'].includes(unmasked.type) ? onlyNumbers(value) : value,
    );

    fieldOnChange(masked.masked);
    onSelectAccount(null);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      searchAccounts(unmasked.raw || value);
    }, 300);
  };

  const handleSelect = (account: AccountResult, fieldOnChange: (v: string) => void) => {
    if (account.pix) {
      const masked = maskPixKey(account.pix);
      fieldOnChange(masked.masked);

      const startNumber = onlyNumbers(masked.masked).substring(0, 2);
      if (masked.type === 'CPF' && !isNaN(Number(startNumber)) && +startNumber > 10 && +startNumber < 60) {
        setPixTypeKey(null);
      } else if (masked.type === 'PHONE') {
        setPixTypeKey('PHONE');
      } else if (masked.type === 'CPF') {
        setPixTypeKey('CPF');
      } else {
        setPixTypeKey(null);
      }
    }

    onSelectAccount(account);
    setShowDropdown(false);
    setSuggestions([]);
  };

  return (
    <Box ref={containerRef} position="relative">
      <Controller
        name={name}
        control={control}
        rules={registerOptions}
        defaultValue=""
        render={({ field, formState: { isSubmitting }, fieldState: { error } }) => (
          <FormControl isInvalid={!!error}>
            <FormLabelCustom label={label} name={name} props={{}} />
            <Box position="relative">
              <Input
                {...field}
                id={name}
                isDisabled={isSubmitting}
                placeholder="Digite a chave PIX ou nome do favorecido"
                variant="auth"
                fontSize="sm"
                size="sm"
                rounded="sm"
                onChange={(e) => handleInputChange(e.target.value, field.onChange)}
                onFocus={() => {
                  if (suggestions.length > 0) setShowDropdown(true);
                }}
                autoComplete="off"
              />
              {loading && (
                <Spinner size="xs" position="absolute" right="10px" top="50%" transform="translateY(-50%)" />
              )}
            </Box>

            {showDropdown && suggestions.length > 0 && (
              <List
                position="absolute"
                zIndex={10}
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="md"
                mt={1}
                w="full"
                boxShadow="md"
                maxH="200px"
                overflowY="auto"
              >
                {suggestions.map((account) => (
                  <ListItem
                    key={account.id}
                    px={3}
                    py={2}
                    cursor="pointer"
                    _hover={{ bg: 'gray.50' }}
                    onClick={() => handleSelect(account, field.onChange)}
                  >
                    <Text fontSize="sm" fontWeight="medium">
                      {account.accountHolder || '---'}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      {account.pix || `${account.agency} - ${account.account}`}
                    </Text>
                  </ListItem>
                ))}
              </List>
            )}

            <FormErrorMessage>{error && error.message}</FormErrorMessage>
          </FormControl>
        )}
      />
    </Box>
  );
}
